<template>
    <section>
      <form id="controller" @reset.prevent="reset">
        <div id="intro">
          <p class="lede">
            Use the filters to choose which Baltimore homicides since 2007 appear on the map. Click or tap a point for more info on that homicide.
          </p>
          <div class="search-btns">
            <input type="reset" value="RESET">
          </div>
          <div id="filters" :class="{ 'mobile-open': showMobile }">
            <div class="mobile-filters-toggle" @click="showMobile = !showMobile">
              FILTERS
              <span>
              </span>
            </div>
            <fieldset>
              <div class="filter-column">
                <Selector
                  label="Time frame"
                  name="range"
                  :values="timeRanges"
                  :selected="range"
                  @update="route"
                />
                <Selector
                  label="District"
                  name="district"
                  :values="districts"
                  :selected="district"
                  @update="route"
                />
                <Selector
                  label="ZIP code"
                  name="zipcode"
                  :values="zipcodes"
                  :selected="zipcode"
                  @update="route"
                />
                <Selector
                  label="Cause of death"
                  name="cause"
                  :values="causes"
                  :selected="cause"
                  @update="route"
                />
              </div>
              <div class="filter-column">
                <Selector
                  label="Age"
                  name="age"
                  :values="ages"
                  :selected="age"
                  @update="route"
                />
                <Selector
                  label="Gender"
                  name="gender"
                  :values="genders"
                  :selected="gender"
                  @update="route"
                />
                <Selector
                  label="Race"
                  name="race"
                  :values="races"
                  :selected="race"
                  @update="route"
                />
                <div class="dropdown">
                  <label for="searchfilter">
                    Name
                  </label>
                  <input id="searchfilter" v-model="searchName" type="text" autocomplete="off">
                </div>
              </div>
              <div id="mobile-btns">
                <input type="reset" value="RESET">
              </div>
            </fieldset>
          </div>
        </div>
      </form>
      <div v-if="loadingMsg" class="search-results-total">
        {{ loadingMsg }}
      </div>
      <div v-else class="search-results-total">
        {{ victims.length | localize }} reported homicide{{ victims | pluralize }} fit{{ victims | agreement }} the search criteria.
        <span v-if="allVictimMarkers.length > victimMarkers.length">
          <br>
          Warning: Too many results to display.
          Map has been limited to first {{ victimMarkers.length }} results.
        </span>
      </div>
      <div id="map">
        <l-map
          class="leaflet-map"
          :center="center"
          :zoom="zoom"
          :min-zoom="11"
          :max-zoom="16"
          :max-bounds="bounds"
          :options="{scrollWheelZoom:false}"
          ref="map"
        >
          <l-tile-layer :url="url" :attribution="attribution" :options="{subdomains}"></l-tile-layer>
          <VictimMarker
            v-for="marker in victimMarkers"
            :key="marker.id"
            :marker="marker"
          />
        </l-map>
      </div>
      <dl id="key">
        <dt>
          Map key:
        </dt>
        <dd v-for="cause in causeLegend" :key="cause.name">
          <img :src="cause.icon" width="12" height="20" alt="map marker">
          = {{ cause.name }}
        </dd>
      </dl>
  </section>
</template>

<script>
import L from "leaflet";
import { LMap, LTileLayer } from "vue2-leaflet";

import { DateTime } from "luxon";

import { orderedMatch } from "../../common/text.js";
import VictimLoader from "../mixins/VictimLoader.vue";

import Selector from "./Selector.vue";
import VictimMarker from "./VictimMarker.vue";

// Ignore timezones
DateTime.local().setZone("UTC");

let baseIcon = {
  height: 20,
  width: 12,
  iconAnchor: [6, 20],
  popupAnchor: [0, -15],
  tooltipAnchor: [6, -12]
};

let icons = {
  asphyxiation: require("../img/mm_20_blue.png"),
  bluntforce: require("../img/mm_20_green.png"),
  shooting: require("../img/mm_20_red.png"),
  stabbing: require("../img/mm_20_black.png"),
  unknown: require("../img/mm_20_white.png"),
  other: require("../img/mm_20_yellow.png")
};

export default {
  name: "VictimsHome",
  components: {
    LMap,
    LTileLayer,
    Selector,
    VictimMarker
  },
  mixins: [VictimLoader],
  props: {
    range: { type: String, default: "30" },
    district: { type: String, default: "all" },
    zipcode: { type: String, default: "all" },
    cause: { type: String, default: "all" },
    age: { type: String, default: "all" },
    gender: { type: String, default: "all" },
    race: { type: String, default: "all" },
    maxMarkerCount: { type: Number, default: 500 }
  },
  data() {
    return {
      zoom: 13,
      center: L.latLng(39.2891399125, -76.6165924072),
      bounds: L.latLngBounds([
        [39.151587, -76.930783],
        [39.509025, -76.259315]
      ]),
      url:
        "https://cartodb-basemaps-{s}.global.ssl.fastly.net/rastertiles/voyager/{z}/{x}/{y}{r}.png",
      subdomains: "abcd",
      attribution:
        '&copy; <a href="http://www.openstreetmap.org/copyright">OpenStreetMap</a> &copy; <a href="http://cartodb.com/attributions">CartoDB</a>',
      map: null,
      causeLegend: [
        { name: "Shooting", icon: icons.shooting },
        { name: "Stabbing", icon: icons.stabbing },
        { name: "Blunt Force", icon: icons.bluntforce },
        { name: "Asphyxiation", icon: icons.asphyxiation },
        { name: "Other", icon: icons.other },
        { name: "Unknown", icon: icons.unknown }
      ],
      districts: [
        { val: "all", text: "All" },
        { val: "CD", text: "Central District" },
        { val: "ED", text: "Eastern District" },
        { val: "ND", text: "Northern District" },
        { val: "NE", text: "Northeastern District" },
        { val: "NW", text: "Northwestern District" },
        { val: "SD", text: "Southern District" },
        { val: "SE", text: "Southeastern District" },
        { val: "SW", text: "Southwestern District" },
        { val: "WD", text: "Western District" }
      ],
      zipcodes: [
        { val: "all", text: "All" },
        { val: "21201", text: "21201" },
        { val: "21202", text: "21202" },
        { val: "21205", text: "21205" },
        { val: "21206", text: "21206" },
        { val: "21207", text: "21207" },
        { val: "21208", text: "21208" },
        { val: "21209", text: "21209" },
        { val: "21210", text: "21210" },
        { val: "21211", text: "21211" },
        { val: "21212", text: "21212" },
        { val: "21213", text: "21213" },
        { val: "21214", text: "21214" },
        { val: "21215", text: "21215" },
        { val: "21216", text: "21216" },
        { val: "21217", text: "21217" },
        { val: "21218", text: "21218" },
        { val: "21219", text: "21219" },
        { val: "21222", text: "21222" },
        { val: "21223", text: "21223" },
        { val: "21224", text: "21224" },
        { val: "21225", text: "21225" },
        { val: "21226", text: "21226" },
        { val: "21227", text: "21227" },
        { val: "21228", text: "21228" },
        { val: "21229", text: "21229" },
        { val: "21230", text: "21230" },
        { val: "21231", text: "21231" },
        { val: "21234", text: "21234" },
        { val: "21237", text: "21237" },
        { val: "21239", text: "21239" },
        { val: "21287", text: "21287" }
      ],
      causes: [
        { val: "all", text: "All" },
        { val: "shooting", text: "Shooting" },
        { val: "stabbing", text: "Stabbing" },
        { val: "bluntforce", text: "Blunt Force" },
        { val: "asphyxiation", text: "Asphyxiation" },
        { val: "other", text: "Other" },
        { val: "unknown", text: "Unknown" }
      ],
      ages: [
        { val: "all", text: "All" },
        { val: "under18", text: "under 18" },
        { val: "18to25", text: "18 to 25" },
        { val: "26to34", text: "26 to 34" },
        { val: "35to50", text: "35 to 50" },
        { val: "over50", text: "over 50" }
      ],
      genders: [
        { val: "all", text: "All" },
        { val: "male", text: "Male" },
        { val: "female", text: "Female" },
        { val: "unknown", text: "Unknown" }
      ],
      races: [
        { val: "all", text: "All" },
        { val: "white", text: "White" },
        { val: "black", text: "Black" },
        { val: "asian", text: "Asian" },
        { val: "hispanic", text: "Hispanic" },
        { val: "unknown", text: "Unknown" }
      ],
      searchName: "",
      showMobile: false
    };
  },
  filters: {
    pluralize(arr) {
      return arr.length === 1 ? "" : "s";
    },
    agreement(arr) {
      return arr.length === 1 ? "s" : "";
    },
    localize(n) {
      return n === 0 ? "No" : n.toLocaleString("en");
    }
  },
  mounted() {
    this.$nextTick(() => {
      this.map = this.$refs.map.mapObject;
    });
  },
  methods: {
    dateFilter(victim) {
      if (this.range === "all") {
        // Easter egg feature for seeing all data/crashing your browser
        return true;
      }
      // Doing string comparisions because creating DateTime objects is too
      // expensive to do quickly for all the victims. Fortunately, ISO times
      // sort alphabetically.
      if (this.cutoffDate) {
        return victim.dateFound >= this.cutoffDate;
      }
      return victim.year === this.range;
    },
    ageFilter(victim) {
      switch (this.age) {
        case "all":
          return true;
        case "under18":
          return victim.age !== null && victim.age < 18;
        case "18to25":
          return victim.age !== null && 18 <= victim.age && victim.age <= 25;
        case "26to34":
          return victim.age !== null && 26 <= victim.age && victim.age <= 34;
        case "35to50":
          return victim.age !== null && 35 <= victim.age && victim.age <= 50;
        case "over50":
          return victim.age !== null && 50 < victim.age;
      }
    },
    searchNameFilter(victim) {
      if (!this.searchName) {
        return true;
      }
      return orderedMatch(victim.fullName, this.searchName);
    },
    simpleFilter(attr) {
      return victim =>
        this[attr] == "all" ? true : victim[attr] === this[attr];
    },
    reset() {
      this.searchName = "";
      this.$router.push({ path: "/", query: {} });
      document.title = "Baltimore Homicides";
    },
    route({ name, value, label, text }) {
      this.$router.push({
        path: "/",
        query: { ...this.$route.query, [name]: value }
      });
      document.title = `Baltimore Homicides • ${label} = ${text}`;
    }
  },
  computed: {
    victims() {
      return this.allVictims
        .filter(this.dateFilter)
        .filter(this.ageFilter)
        .filter(this.simpleFilter("zipcode"))
        .filter(this.simpleFilter("district"))
        .filter(this.simpleFilter("cause"))
        .filter(this.simpleFilter("gender"))
        .filter(this.simpleFilter("race"))
        .filter(this.searchNameFilter);
    },
    allVictimMarkers() {
      const round = val => parseFloat(val).toFixed(3);

      // coords lets us condense locations
      let coords = new Map();

      // Consolidate all victims who are at the same location
      this.victims.forEach(victim => {
        let id = `${round(victim.latitude)},${round(victim.longitude)}`;
        if (!coords[id]) {
          coords[id] = {
            id,
            position: [victim.latitude, victim.longitude],
            victims: []
          };
        }
        coords[id].victims.push(victim);
      });
      // Distinguish markers with 1 victim from multi-victim markers
      let markers = Object.values(coords);
      markers.forEach(marker => {
        if (marker.victims.length === 1) {
          let victim = marker.victims[0];
          marker.tooltip = victim.fullName;
          marker.icon = L.icon({
            ...baseIcon,
            iconUrl: icons[victim.cause]
          });
        } else {
          marker.tooltip = "Multiple Victims";
          marker.icon = L.icon({
            ...baseIcon,
            iconUrl: icons["other"]
          });
        }
      });
      return markers;
    },
    victimMarkers() {
      return this.allVictimMarkers.slice(0, this.maxMarkerCount);
    },
    timeRanges() {
      let ranges = [
        { val: "7", text: "Past week" },
        { val: "30", text: "Past 30 days" },
        { val: "90", text: "Past 3 months" },
        { val: "180", text: "Past 6 months" }
      ];
      for (let y = DateTime.local().year; y > 2006; y--) {
        ranges.push({ val: y, text: `All of ${y}` });
      }
      ranges.push({ val: "all", text: "All since 2007" });
      return ranges;
    },
    cutoffDate() {
      return parseInt(this.range) < 2007
        ? DateTime.local()
            .minus({ days: this.range })
            .toISODate()
        : null;
    }
  },
  watch: {
    victimMarkers(val) {
      if (val.length > 0) {
        this.map.fitBounds(val.map(marker => marker.position));
      }
    }
  }
};
</script>

<style lang="scss">
.leaflet-map {
  height: 100%;
  width: 100%;
}
</style>
